/**
 * @file ContentAuthorsPage.tsx
 * @description «Сайт → Авторы» блога (phase12.0; phase19.16 Р1 — карточка
 * автора, uiux-design-r1 §7): список `ResponsiveList` (фото 40, имя, первая
 * строка «о себе», число статей), «Новый автор», панель автора с фото из
 * медиатеки и соцсетями; удаление — окном кабинета вместо системного
 * `confirm()`. Список после правки перечитывается с сервера.
 */

import { UserCircle, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useCurrentContext } from "../../../auth/hooks";
import { AuthorSheet } from "../../../components/content/AuthorSheet";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { useNotify } from "../../../components/ui/NotifyProvider";
import type { ListColumn } from "../../../components/ui/responsive-list-types";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { TwoLineCell } from "../../../components/ui/TwoLineCell";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ApiError } from "../../../lib/api-client";
import { contentAuthors, type ContentAuthor } from "../../../lib/api-content";
import { contentMediaUrl } from "../../../lib/api-content-media";
import { authorInput, type AuthorForm } from "../../../lib/content/author-form";
import { pluralRu } from "../../../lib/plural";

/** Фото 40 — только если есть (SB80: без фото картинки нет). */
function AuthorCell({ author }: { author: ContentAuthor }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      {author.photoMediaId ? (
        <img src={contentMediaUrl(author.photoMediaId)} alt="" className="size-10 shrink-0 rounded-full object-cover" />
      ) : null}
      <TwoLineCell primary={author.name} secondary={author.bio.split("\n")[0]} />
    </span>
  );
}

const COLUMNS: ListColumn<ContentAuthor>[] = [
  { key: "name", header: "Автор", compact: "title", cell: (a) => <AuthorCell author={a} /> },
  {
    key: "articles",
    header: "Статей",
    width: "w-[140px]",
    align: "end",
    compact: "trailing",
    cell: (a) => (a.articlesCount === undefined ? "—" : `${a.articlesCount} ${pluralRu(a.articlesCount, ["статья", "статьи", "статей"])}`),
  },
];

export function ContentAuthorsPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  const [authors, setAuthors] = useState<ContentAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const slug = useCurrentContext()?.shop?.slug ?? "";
  // Открытый автор — в адресе (`?author=id`): строка списка — ссылка, панель можно переслать.
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const open: ContentAuthor | "new" | null = creating ? "new" : (authors.find((a) => a.id === params.get("author")) ?? null);
  const close = () => {
    setCreating(false);
    if (params.has("author")) setParams({}, { replace: true });
  };
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setAuthors(await contentAuthors.list());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить авторов.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onSave(form: AuthorForm) {
    setBusy(true);
    const body = authorInput(form);
    try {
      if (open && open !== "new") {
        await contentAuthors.update(open.id, body);
        notify.success("Автор сохранён");
      } else {
        await contentAuthors.create({
          name: body.name,
          bio: body.bio,
          photoMediaId: body.photoMediaId ?? undefined,
          telegramHandle: body.telegramHandle ?? undefined,
          vkHandle: body.vkHandle ?? undefined,
        });
        notify.success("Автор добавлен");
      }
      close();
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить автора.");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(author: ContentAuthor) {
    const ok = await confirm.confirm({
      title: `Удалить автора «${author.name}»?`,
      description: "Страница автора пропадёт с сайта. Если у автора есть статьи, сервер не даст удалить — сначала смените автора в статьях.",
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await contentAuthors.remove(author.id);
      close();
      notify.success("Автор удалён");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить автора.");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Авторы блога"
        subtitle="Имя, фото и соцсети под статьями и на странице автора"
        action={
          <Button variant="brand" onClick={() => setCreating(true)}>
            <UserPlus aria-hidden />
            Новый автор
          </Button>
        }
      />
      <ResponsiveList
        aria-label="Авторы блога"
        rows={authors}
        columns={COLUMNS}
        rowKey={(a) => a.id}
        rowHref={(a) => `/shop/${slug}/content/authors?author=${a.id}`}
        rowLabel={(a) => `Автор ${a.name}`}
        loading={loading}
        error={error}
        onRetry={() => void refresh()}
        empty={{
          icon: UserCircle,
          title: "Авторов пока нет",
          description: "Автор нужен статье: его имя и фото видны под текстом.",
          action: <Button variant="outline" onClick={() => setCreating(true)}>Добавить автора</Button>,
        }}
      />
      <AuthorSheet author={open} busy={busy} onClose={close} onSave={(f) => void onSave(f)} onDelete={(a) => void onDelete(a)} />
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
    </div>
  );
}
